import { useEffect, useRef, useState } from 'react';
import { getReferencePlaces } from '../../services/referencePlacesService';
import {
  bboxExceedsMaxSpan,
  bboxFromLngLatBounds,
  buildReferencePlacesQuery,
  formatReferencePlacesBboxParam,
  readReferencePlacesResponse,
  REFERENCE_PLACES_DEFAULT_LIMIT,
  REFERENCE_PLACES_MOVE_DEBOUNCE_MS,
} from '../utils/referencePlaces';

/**
 * Pines del viewport. Recarga en pan/zoom (no el catálogo). No llama si
 * no hay kinds prendidos o la caja supera 2°.
 *
 * @param {object} options
 * @param {React.MutableRefObject} options.mapInstanceRef
 * @param {boolean} options.enabled
 * @param {string[]} options.kinds
 * @param {number} [options.mapAttachVersion]
 */
export function useReferencePlacesViewport({
  mapInstanceRef,
  enabled = false,
  kinds = [],
  mapAttachVersion = 0,
}) {
  const [places, setPlaces] = useState([]);
  const [truncated, setTruncated] = useState(false);
  const [viewportTooLarge, setViewportTooLarge] = useState(false);
  const abortRef = useRef(null);
  const debounceRef = useRef(null);
  const kindsKey = (kinds || []).join(',');

  useEffect(() => {
    const clearTimer = () => {
      if (debounceRef.current) {
        window.clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
    };
    const abortInflight = () => {
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
    };

    if (!enabled || !kindsKey) {
      abortInflight();
      clearTimer();
      setPlaces([]);
      setTruncated(false);
      setViewportTooLarge(false);
      return undefined;
    }

    const runFetch = () => {
      const map = mapInstanceRef.current;
      if (!map || typeof map.getBounds !== 'function') {
        return;
      }

      const bbox = bboxFromLngLatBounds(map.getBounds());
      if (!bbox || bboxExceedsMaxSpan(bbox)) {
        abortInflight();
        setPlaces([]);
        setTruncated(false);
        setViewportTooLarge(true);
        return;
      }

      setViewportTooLarge(false);
      abortInflight();
      const controller = new AbortController();
      abortRef.current = controller;

      const query = {
        bbox: formatReferencePlacesBboxParam(bbox),
        ...buildReferencePlacesQuery({
          kinds: kindsKey.split(','),
          limit: REFERENCE_PLACES_DEFAULT_LIMIT,
        }),
      };

      getReferencePlaces(query, { signal: controller.signal })
        .then((response) => {
          if (controller.signal.aborted) return;
          if (!response?._httpOk) {
            if (response?._httpStatus === 400) {
              setPlaces([]);
              setTruncated(false);
              setViewportTooLarge(true);
              return;
            }
            console.error(
              'No se pudieron cargar los lugares de referencia',
              response?._httpStatus,
              response?.message,
            );
            return;
          }
          const read = readReferencePlacesResponse(response);
          setPlaces(read.places);
          setTruncated(read.truncated);
        })
        .catch((error) => {
          if (error?.name === 'AbortError') return;
          console.error('No se pudieron cargar los lugares de referencia', error);
        });
    };

    const scheduleFetch = () => {
      clearTimer();
      debounceRef.current = window.setTimeout(runFetch, REFERENCE_PLACES_MOVE_DEBOUNCE_MS);
    };

    runFetch();

    const map = mapInstanceRef.current;
    if (!map) return () => {
      abortInflight();
      clearTimer();
    };

    map.on('moveend', scheduleFetch);
    return () => {
      abortInflight();
      clearTimer();
      try { map.off('moveend', scheduleFetch); } catch (_) { /* mapa destruyéndose */ }
    };
  }, [enabled, kindsKey, mapAttachVersion, mapInstanceRef]);

  return { places, truncated, viewportTooLarge };
}
